import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';

const PrivacyPolicy = ({ onClose }) => {
  // Lock body scroll when modal is open
  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  const modalContent = (
    <div 
      className="fixed inset-0 z-[99999]"
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 99999
      }}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60"
        onClick={handleClose}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)'
        }}
      />
      
      {/* Modal Container */}
      <div 
        className="absolute inset-0 overflow-y-auto overscroll-contain"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <div className="min-h-full p-3 sm:p-4 flex items-start justify-center">
          <div 
            className="bg-white rounded-lg shadow-2xl w-full max-w-4xl relative my-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close X Button - Top Right */}
            <button
              type="button"
              onClick={handleClose}
              className="absolute top-2 right-2 sm:top-3 sm:right-3 z-10 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-gray-800 hover:bg-gray-900 text-white transition-all text-xl sm:text-2xl font-bold shadow-lg"
              aria-label="Close"
            >
              ✕
            </button>

            {/* Header */}
            <div className="bg-wood-brown text-white px-4 sm:px-6 py-3 sm:py-4 rounded-t-lg">
              <h1 className="text-xl sm:text-2xl font-bold pr-12">Privacy Policy</h1>
            </div>

            {/* Content */}
            <div className="px-4 sm:px-6 py-6 sm:py-8 max-w-none">
              <div className="text-center mb-6 sm:mb-8">
                <h2 className="text-xl sm:text-2xl font-bold text-wood-dark">Easy Floor KC | easyfloorkc.com</h2>
                <p className="text-gray-600">Privacy Policy</p>
                <p className="text-gray-600">Easy Floor KC (easyfloorkc.com)</p>
                <p className="text-sm text-gray-500 mt-2">Effective date: January 6, 2026</p>
                <p className="text-sm text-gray-500">Last updated: January 6, 2026</p>
              </div>

              {/* Section 1 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">1) Who we are</h3>
                <p className="text-gray-700 mb-4 text-sm sm:text-base">
                  This Privacy Policy describes how Easy Floor KC ("Easy Floor KC," "we," "us," or "our") collects, uses, and shares information when you visit easyfloorkc.com or contact us about our flooring services.
                </p>
                <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                  <p className="font-semibold text-gray-800 mb-2">Contact:</p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1 text-sm sm:text-base">
                    <li>Website: easyfloorkc.com</li>
                    <li>Phone: (913) 538-8541</li>
                  </ul>
                </div>
              </section>

              {/* Section 2 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">2) Information we collect</h3>
                <p className="text-gray-700 mb-4 text-sm sm:text-base">We may collect the following categories of information:</p>
                
                <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">A. Information you provide to us</h4>
                <ul className="list-disc list-inside text-gray-700 space-y-1 mb-4 ml-2 sm:ml-4 text-sm sm:text-base">
                  <li>Contact details (name, phone number, email address, address)</li>
                  <li>Project details (type of flooring, measurements, photos you share, timeline, budget range, jobsite notes)</li>
                  <li>Communications (messages, emails, calls, texts, and form submissions)</li>
                  <li>Scheduling details (requested appointment times, access instructions)</li>
                </ul>

                <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">B. Information collected automatically</h4>
                <p className="text-gray-700 mb-2 text-sm sm:text-base">When you visit our website, we (and our service providers) may automatically collect:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 mb-4 ml-2 sm:ml-4 text-sm sm:text-base">
                  <li>Device and usage information (IP address, device type, browser, pages viewed, approximate location inferred from IP)</li>
                  <li>Cookies and similar tracking technologies (see Section 6)</li>
                </ul>

                <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">C. Payment information</h4>
                <p className="text-gray-700 text-sm sm:text-base">
                  If you pay us, payments may be processed by third-party processors. We typically do not store full payment card numbers ourselves; the payment processor handles that data per their own policies.
                </p>
              </section>

              {/* Section 3 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">3) How we use information</h3>
                <p className="text-gray-700 mb-2 text-sm sm:text-base">We use the information we collect to:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-2 sm:ml-4 text-sm sm:text-base">
                  <li>Provide quotes, schedule appointments, and deliver services</li>
                  <li>Communicate with you about your request (including confirmations and updates)</li>
                  <li>Improve our website, services, and customer experience</li>
                  <li>Protect against fraud, abuse, and security incidents</li>
                  <li>Comply with legal obligations and enforce our agreements</li>
                </ul>
              </section>

              {/* Section 4 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">4) How we share information</h3>
                <p className="text-gray-700 mb-2 text-sm sm:text-base">We may share information with:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 mb-4 ml-2 sm:ml-4 text-sm sm:text-base">
                  <li>Service providers who help us operate (hosting, analytics, CRM, communications tools, payment processing)</li>
                  <li>Contractors/subcontractors as needed to provide services (for example, coordinating scheduling or onsite work)</li>
                  <li>Legal or compliance parties if required to comply with law or respond to lawful requests</li>
                  <li>Business transfers (if we undergo a merger, acquisition, or sale)</li>
                </ul>
                <p className="text-gray-700 font-semibold text-sm sm:text-base">We do not sell your personal information.</p>
              </section>

              {/* Section 5 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">5) Text messaging and phone communications (SMS)</h3>
                <p className="text-gray-700 mb-4 text-sm sm:text-base">
                  If you provide your phone number, you may receive calls or texts from us for purposes such as quotes, scheduling, confirmations, project updates, customer support, and (if you opted in) occasional promotional messages.
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 mb-4 ml-2 sm:ml-4 text-sm sm:text-base">
                  <li><strong>Opt-out:</strong> Reply STOP to unsubscribe from text messages.</li>
                  <li><strong>Help:</strong> Reply HELP for help.</li>
                  <li><strong>Message frequency:</strong> Varies.</li>
                  <li><strong>Rates:</strong> Message and data rates may apply.</li>
                </ul>
                <p className="text-gray-700 text-sm sm:text-base">
                  <strong>Mobile information sharing:</strong> We do not share or sell mobile subscriber information with third parties for their marketing or promotional purposes. Your text messaging opt-in data and consent will not be shared with third parties except as necessary to provide the messaging service (for example, via our communications/CRM providers).
                </p>
              </section>

              {/* Section 6 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">6) Cookies and analytics</h3>
                <p className="text-gray-700 mb-4 text-sm sm:text-base">
                  We may use cookies and similar technologies to remember preferences, understand website traffic and performance, and improve marketing and site functionality.
                </p>
                <p className="text-gray-700 text-sm sm:text-base">
                  You can control cookies through your browser settings. Note that disabling cookies may affect site functionality.
                </p>
              </section>

              {/* Section 7 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">7) Data retention</h3>
                <p className="text-gray-700 text-sm sm:text-base">
                  We keep personal information only as long as necessary for the purposes described in this policy, including for legal, accounting, or dispute-resolution needs.
                </p>
              </section>

              {/* Section 8 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">8) Security</h3>
                <p className="text-gray-700 text-sm sm:text-base">
                  We use reasonable administrative, technical, and physical safeguards designed to protect your information. No method of transmission or storage is 100% secure, so we cannot guarantee absolute security.
                </p>
              </section>

              {/* Section 9 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">9) Children's privacy</h3>
                <p className="text-gray-700 text-sm sm:text-base">
                  Our website and services are not directed to children under 13, and we do not knowingly collect personal information from children under 13.
                </p>
              </section>

              {/* Section 10 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">10) Your choices</h3>
                <p className="text-gray-700 mb-4 text-sm sm:text-base">
                  Depending on your location and applicable law, you may request to access, correct, or delete your personal information; opt out of marketing communications; or request information about how we use or share your data.
                </p>
                <p className="text-gray-700 text-sm sm:text-base">
                  To make a request, contact us using the contact information above.
                </p>
              </section>

              {/* Section 11 */}
              <section className="mb-6 sm:mb-8">
                <h3 className="text-lg sm:text-xl font-bold text-wood-dark mb-3 sm:mb-4">11) Changes to this Privacy Policy</h3>
                <p className="text-gray-700 text-sm sm:text-base">
                  We may update this policy from time to time. The "Last updated" date reflects the most recent revision. Continued use of the website after changes means you accept the updated policy.
                </p>
              </section>
            </div>

            {/* Footer */}
            <div className="bg-gray-100 px-4 sm:px-6 py-3 sm:py-4 rounded-b-lg border-t">
              <button
                type="button"
                onClick={handleClose}
                className="w-full bg-wood-brown hover:bg-wood-dark text-white font-semibold py-3 px-6 rounded-lg transition-colors text-sm sm:text-base"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal to render modal at document body level
  return ReactDOM.createPortal(modalContent, document.body);
};

export default PrivacyPolicy;
